import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import {
  Sparkles, Clock, Factory, ListTodo, RefreshCw,
  ChevronRight, ChevronLeft, Rocket, SkipForward,
  ShieldCheck, Wifi, CloudOff, ScanBarcode,
  Timer, List, Layers, Box, Play,
  FileEdit, Users, BarChart3, CloudUpload, Grid3X3,
  ArrowRight, Sun, Moon,
} from 'lucide-react';
import OperisLogo from '../../components/common/OperisLogo';
import AnimatedClock from '../../components/common/AnimatedClock';
import WaveRiver from '../../components/landing/WaveRiver';

/* ══════════════════════════════════════════════
   STEP DEFINITIONS
   ══════════════════════════════════════════════ */
const STEPS = [
  {
    key: 'welcome',
    icon: Sparkles,
    title: 'Welcome to Operis',
    subtitle: 'Your SAP-connected time management platform',
    description:
      'Operis streamlines workforce time tracking with real-time SAP ByDesign integration. ' +
      'Track hours, manage production tasks, and sync everything seamlessly — even offline.',
    features: [
      { icon: ShieldCheck, text: 'SAP ByDesign Certified' },
      { icon: Wifi, text: 'Real-time Sync' },
      { icon: CloudOff, text: 'Works Offline' },
    ],
    gradient: 'from-amber-500 to-orange-600',
  },
  {
    key: 'clock',
    icon: Clock,
    title: 'Clock In & Out',
    subtitle: 'Start and stop time entries with one tap',
    description:
      'Use the Clock In/Out screen to start a timer against a production lot and activity. ' +
      'Scan a barcode or select manually. Your elapsed time is tracked live with a beautiful analog clock. ' +
      'Previous sessions are saved and viewable anytime.',
    features: [
      { icon: ScanBarcode, text: 'Barcode scanning' },
      { icon: Timer, text: 'Live timer tracking' },
      { icon: List, text: 'Session history' },
    ],
    gradient: 'from-amber-600 to-amber-500',
  },
  {
    key: 'production',
    icon: Factory,
    title: 'Production & Lots',
    subtitle: 'Browse and manage production tasks',
    description:
      'View all production lots synced from SAP ByDesign. Drill into lot details, see material inputs/outputs, ' +
      'confirmation groups, and activity times. Start or complete production tasks directly from the app.',
    features: [
      { icon: Layers, text: 'Production lot details' },
      { icon: Box, text: 'Material tracking' },
      { icon: Play, text: 'Start & complete tasks' },
    ],
    gradient: 'from-orange-500 to-amber-600',
  },
  {
    key: 'tasks',
    icon: ListTodo,
    title: 'Tasks & Entries',
    subtitle: 'Manage your work and time records',
    description:
      'My Tasks shows your assigned work items. Time Entries lists all clock-in records with duration, ' +
      'lot, and activity details. Edit, delete, or create manual entries. Everything syncs to SAP automatically.',
    features: [
      { icon: FileEdit, text: 'Create & edit entries' },
      { icon: Users, text: 'Team task assignment' },
      { icon: BarChart3, text: 'Time analytics' },
    ],
    gradient: 'from-amber-500 to-yellow-500',
  },
  {
    key: 'sync',
    icon: RefreshCw,
    title: 'Sync & Offline',
    subtitle: 'Stay productive without connectivity',
    description:
      'The Sync Manager handles all data exchange with SAP. Work offline and your changes queue up automatically. ' +
      'When connectivity returns, everything syncs in the background. The Dashboard gives you an overview of all activity.',
    features: [
      { icon: CloudUpload, text: 'Auto background sync' },
      { icon: Wifi, text: 'Offline queue' },
      { icon: Grid3X3, text: 'Dashboard overview' },
    ],
    gradient: 'from-orange-600 to-amber-500',
  },
];

/* ══════════════════════════════════════════════
   FLOATING TIME PARTICLES
   ══════════════════════════════════════════════ */
const floatingTimes = [
  { text: '08:30', x: 5,  y: 12, delay: 0, dur: 7 },
  { text: '14:22', x: 88, y: 8,  delay: 2, dur: 9 },
  { text: '17:45', x: 92, y: 65, delay: 4, dur: 8 },
  { text: '23:00', x: 4,  y: 78, delay: 1, dur: 6 },
  { text: '12:00', x: 50, y: 5,  delay: 3, dur: 10 },
];

/* ══════════════════════════════════════════════
   ONBOARDING PAGE
   ══════════════════════════════════════════════ */
const OnboardingPage = () => {
  const { darkMode, toggleTheme } = useTheme();
  const { completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRevisit = searchParams.get('revisit') === 'true';

  const [currentStep, setCurrentStep] = useState(0);
  const contentRef = useRef(null);
  const [animating, setAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState('right');

  const step = STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === STEPS.length - 1;
  const Icon = step.icon;

  /* ── Animate step transitions ── */
  const changeStep = useCallback((newIndex, direction) => {
    if (animating) return;
    setSlideDirection(direction);
    setAnimating(true);
    // Fade out
    if (contentRef.current) contentRef.current.style.opacity = '0';
    setTimeout(() => {
      setCurrentStep(newIndex);
      if (contentRef.current) contentRef.current.style.opacity = '1';
      setAnimating(false);
    }, 250);
  }, [animating]);

  const goNext = useCallback(() => {
    if (!isLast) changeStep(currentStep + 1, 'right');
  }, [currentStep, isLast, changeStep]);

  const goBack = useCallback(() => {
    if (!isFirst) changeStep(currentStep - 1, 'left');
  }, [currentStep, isFirst, changeStep]);

  const handleFinish = useCallback(() => {
    if (!isRevisit) {
      completeOnboarding();
    }
    navigate('/dashboard');
  }, [navigate, isRevisit, completeOnboarding]);

  const handleSkip = useCallback(() => {
    handleFinish();
  }, [handleFinish]);

  /* ── Keyboard navigation ── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (isLast) handleFinish();
        else goNext();
      } else if (e.key === 'ArrowLeft') {
        goBack();
      } else if (e.key === 'Escape') {
        handleSkip();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goBack, handleFinish, handleSkip, isLast]);

  /* ── Theme tokens ── */
  const bg = darkMode ? 'bg-[#0f0f14]' : 'bg-gray-50';
  const gradientOverlay = darkMode
    ? 'bg-gradient-to-br from-[#0f0f14] via-[#1a1a24] to-[#2a1f0a]/70'
    : 'bg-gradient-to-br from-gray-50 via-white to-amber-50/50';
  const dotColor = darkMode ? 'rgba(217,119,6,0.05)' : 'rgba(245,158,11,0.06)';
  const particleColor = darkMode ? 'text-amber-400' : 'text-amber-500';

  const cardBg = darkMode
    ? 'border-amber-500/[0.12] bg-white/[0.03]'
    : 'border-gray-200/70 bg-white/90 shadow-xl';
  const headingColor = darkMode ? 'text-white' : 'text-gray-900';
  const subColor = darkMode ? 'text-amber-400' : 'text-amber-600';
  const bodyColor = darkMode ? 'text-white/50' : 'text-gray-500';
  const featureBg = darkMode ? 'bg-white/[0.04]' : 'bg-amber-50/60';
  const featureText = darkMode ? 'text-white/60' : 'text-gray-600';
  const featureIcon = darkMode ? 'text-amber-400' : 'text-amber-500';
  const badgeColor = darkMode
    ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
    : 'bg-amber-50 text-amber-600 border-amber-200/50';
  const skipColor = darkMode ? 'text-white/30 hover:text-white/50' : 'text-gray-400 hover:text-gray-600';
  const dotActive = darkMode ? 'bg-amber-400' : 'bg-amber-600';
  const dotInactive = darkMode ? 'bg-white/10' : 'bg-gray-300';
  const backBtnClass = darkMode
    ? 'border-white/10 bg-white/[0.03] text-white/50 hover:bg-white/[0.06] hover:text-white/70'
    : 'border-gray-200 bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600';
  const progressBg = darkMode ? 'bg-white/[0.06]' : 'bg-gray-200';
  const progressFill = 'bg-gradient-to-r from-amber-500 to-amber-600';
  const footerColor = darkMode ? 'text-white/20' : 'text-gray-300';

  return (
    <div className={`relative min-h-screen overflow-hidden transition-colors duration-500 ${bg}`}>
      {/* ── Background ── */}
      <div className={`absolute inset-0 transition-colors duration-500 ${gradientOverlay}`} />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(${dotColor} 1px, transparent 1px)`,
          backgroundSize: '32px 32px',
        }}
      />
      <div
        className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          width: 900, height: 900,
          background: darkMode
            ? 'radial-gradient(circle, rgba(217,119,6,0.08) 0%, transparent 55%)'
            : 'radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 50%)',
        }}
      />
      <WaveRiver darkMode={darkMode} />
      {floatingTimes.map((p, i) => (
        <div
          key={i}
          className={`absolute pointer-events-none select-none font-mono text-xs ${particleColor}`}
          style={{ left: `${p.x}%`, top: `${p.y}%`, opacity: 0.06, animation: `float ${p.dur}s ease-in-out ${p.delay}s infinite` }}
        >
          {p.text}
        </div>
      ))}

      {/* ── Content ── */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col px-5 sm:px-8">
        {/* ── Header ── */}
        <nav className="flex items-center justify-between py-6">
          <OperisLogo size="md" light={darkMode} />
          <div className="flex items-center gap-3">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className={`rounded-lg p-2 transition-all duration-300 ${
                darkMode
                  ? 'text-white/60 hover:text-white hover:bg-white/10'
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
              }`}
              aria-label="Toggle theme"
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            {!isLast && (
              <button
                onClick={handleSkip}
                className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 ${skipColor}`}
              >
                Skip tutorial
                <SkipForward className="h-4 w-4" />
              </button>
            )}
          </div>
        </nav>

        {/* ── Progress bar ── */}
        <div className={`h-1 rounded-full overflow-hidden ${progressBg}`}>
          <div
            className={`h-full rounded-full transition-all duration-500 ease-out ${progressFill}`}
            style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        {/* ── Main content area ── */}
        <div className="flex flex-1 items-center justify-center py-8 lg:py-12">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16 w-full">
            {/* ── Left: Clock illustration ── */}
            <div className="hidden lg:flex flex-col items-center justify-center">
              <div className="relative flex items-center justify-center">
                {/* Rotating rings */}
                {[280, 320, 360].map((d, i) => (
                  <div
                    key={d}
                    className="absolute pointer-events-none rounded-full"
                    style={{
                      width: d, height: d,
                      top: '50%', left: '50%',
                      marginTop: -d / 2, marginLeft: -d / 2,
                      border: `1px ${i % 2 === 0 ? 'dashed' : 'solid'} ${darkMode ? ['#d97706', '#f59e0b', '#ea580c'][i] : '#d97706'}`,
                      opacity: darkMode ? [0.22, 0.16, 0.12][i] : 0.08 - i * 0.02,
                      animation: `spin ${30 + i * 15}s linear infinite${i % 2 === 0 ? '' : ' reverse'}`,
                    }}
                  />
                ))}
                <AnimatedClock
                  size={220}
                  compact
                  showDigital={false}
                  glowColor={darkMode ? '#d97706' : '#d97706'}
                  lightDial={!darkMode}
                />
              </div>

              {/* Step indicator text */}
              <p className={`mt-8 text-sm font-medium ${darkMode ? 'text-white/30' : 'text-gray-400'}`}>
                {isFirst ? 'Getting started' : `Step ${currentStep} of ${STEPS.length - 1}`}
              </p>
            </div>

            {/* ── Right: Step card ── */}
            <div
              ref={contentRef}
              className="transition-opacity duration-250 ease-in-out"
              style={{ opacity: 1 }}
            >
              <div className={`rounded-3xl border p-8 sm:p-10 transition-colors duration-500 ${cardBg}`}>
                {/* Badge */}
                <div className={`mb-6 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold ${badgeColor}`}>
                  {isFirst ? '👋 Welcome' : `Step ${currentStep} of ${STEPS.length - 1}`}
                </div>

                {/* Icon */}
                <div className={`mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${step.gradient} shadow-lg`}>
                  <Icon className="h-8 w-8 text-white" />
                </div>

                {/* Title + subtitle */}
                <h2 className={`text-2xl font-extrabold sm:text-3xl ${headingColor}`}>
                  {step.title}
                </h2>
                <p className={`mt-2 text-sm font-semibold ${subColor}`}>
                  {step.subtitle}
                </p>

                {/* Description */}
                <p className={`mt-5 text-sm leading-relaxed ${bodyColor}`}>
                  {step.description}
                </p>

                {/* Feature pills */}
                <div className="mt-7 space-y-2.5">
                  {step.features.map((f, i) => {
                    const FIcon = f.icon;
                    return (
                      <div
                        key={i}
                        className={`flex items-center gap-3 rounded-xl px-4 py-3 transition-colors ${featureBg}`}
                      >
                        <FIcon className={`h-4.5 w-4.5 flex-shrink-0 ${featureIcon}`} />
                        <span className={`text-sm font-medium ${featureText}`}>{f.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom controls ── */}
        <div className="pb-8">
          {/* Dots */}
          <div className="mb-6 flex justify-center gap-2">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => changeStep(i, i > currentStep ? 'right' : 'left')}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === currentStep
                    ? `w-8 ${dotActive}`
                    : `w-2 ${dotInactive} hover:opacity-60`
                }`}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 max-w-md mx-auto">
            {!isFirst ? (
              <button
                onClick={goBack}
                className={`flex h-12 w-12 items-center justify-center rounded-xl border transition-all duration-300 ${backBtnClass}`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            ) : (
              <div className="w-12" />
            )}

            <button
              onClick={isLast ? handleFinish : goNext}
              className={`flex flex-1 items-center justify-center gap-2.5 rounded-xl py-3.5 px-6 font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 ${
                darkMode
                  ? 'bg-gradient-to-r from-amber-600 to-amber-500 shadow-amber-500/20 hover:shadow-amber-500/30'
                  : 'bg-gradient-to-r from-amber-500 to-amber-600 shadow-amber-500/20 hover:shadow-amber-500/30'
              }`}
            >
              {isLast ? (
                <>
                  <Rocket className="h-5 w-5" />
                  {isRevisit ? 'Back to Dashboard' : "Let's Go!"}
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>
          </div>

          {/* Footer */}
          <p className={`mt-6 text-center text-xs ${footerColor}`}>
            Use arrow keys ← → to navigate &middot; Press Esc to skip
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
